import { useState, useCallback, useEffect, useRef } from 'react';
import type { NameSuggestion, SuggestNameFn, SuggestNameResponse } from '../../../../features/admin/actors/api';
import { isAbortError } from '../../../../services/asyncJobApi';
import type { WizardData } from '../ActorGenerationWizard';
import { downscaleBase64Image } from '../../../../utils/imageUtils';
import styles from './Steps.module.css';
import '../../../../styles/generation-common.css';

// 名前提案に送る画像の長辺 (px)。人物の印象を掴むには十分で、
// フル解像度の PNG をそのまま往復させるより明確に速い
// (backend 側でも同じ長辺に再縮小している)。
const NAME_SUGGEST_IMAGE_MAX_SIZE = 768;

type Props = {
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
  suggestNameApi: SuggestNameFn;
  /** 先読み結果の有効性を判定するキー (生成条件が変わると別キーになる) */
  prefetchKey?: string;
  /** 画像生成と並行して取得済みの名前提案を 1 度だけ取り出す */
  takePrefetchedNames?: (key: string) => Promise<SuggestNameResponse | null> | null;
};

// 内部名を自動生成（英語表示名からローマ字変換）
function generateInternalName(displayNameEn: string): string {
  // 簡易的な処理：空白をアンダースコアに、英数字以外を除去
  const cleaned = displayNameEn
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

  if (!cleaned || !/^[a-z]/.test(cleaned)) {
    return 'model_' + Date.now().toString(36);
  }

  return cleaned;
}

export function AttributeEditStep({ data, onUpdate, suggestNameApi, prefetchKey, takePrefetchedNames }: Props) {
  const [isSuggestingName, setIsSuggestingName] = useState(false);
  const [nameSuggestions, setNameSuggestions] = useState<NameSuggestion[]>([]);
  const [selectedNameIndex, setSelectedNameIndex] = useState<number | null>(null);
  const [isHoveringCancel, setIsHoveringCancel] = useState(false);
  // タグ入力用のローカルstate（編集中のテキストを保持）
  const [tagsInput, setTagsInput] = useState(data.tags.join(', '));

  // AbortController for cancelling name suggestion
  const abortControllerRef = useRef<AbortController | null>(null);

  // キャンセル処理
  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsSuggestingName(false);
    setIsHoveringCancel(false);
  }, []);

  // 名前と属性をAIに提案させる
  // usePrefetched: 画像生成と並行して先読みした結果を使ってよいか
  // (自動実行のみ true。「再生成」ボタンでは同じ候補が返らないよう常に取り直す)
  const handleSuggestName = useCallback(async (options?: { usePrefetched?: boolean }) => {
    const prefetched = options?.usePrefetched && prefetchKey
      ? takePrefetchedNames?.(prefetchKey) ?? null
      : null;

    if (!prefetched && !data.personImageBase64) return;

    // 既存のリクエストをキャンセル
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 新しいAbortControllerを作成
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;

    setIsSuggestingName(true);
    try {
      // 先読みが完了していればそれを使う (画像生成の裏で終わっているはず)
      let result = prefetched ? await prefetched : null;

      // 先読みが無い / 失敗した場合は画像を送る通常経路にフォールバック
      if (!result) {
        if (!data.personImageBase64) return;

        if (data.personMaterialId) {
          // AI生成の結果は素材 ID を参照で渡す (縮小はバックエンド側で行う)
          result = await suggestNameApi({
            image_material_id: data.personMaterialId,
            ethnicity: data.ethnicity,
            generation_prompt: data.prompt || undefined,
          }, { signal });
        } else {
          // アップロード画像は base64 を送る (バックエンドが一時素材化する)。
          // 解析用なので原寸である必要はない。アップロード量を減らして待ち時間を縮める
          // (縮小に失敗したら元画像のまま送る)
          let analysisBase64 = data.personImageBase64;
          try {
            const downscaled = await downscaleBase64Image(
              data.personImageBase64,
              data.personMimeType || 'image/png',
              NAME_SUGGEST_IMAGE_MAX_SIZE,
              { outputMimeType: 'image/jpeg', quality: 0.85 },
            );
            analysisBase64 = downscaled.base64;
          } catch (downscaleErr) {
            console.warn('Failed to downscale image for name suggestion:', downscaleErr);
          }

          result = await suggestNameApi({
            image_base64: analysisBase64,
            ethnicity: data.ethnicity,
            generation_prompt: data.prompt || undefined,
          }, { signal });
        }
      }

      // 名前候補を保存
      if (result.name_suggestions && result.name_suggestions.length > 0) {
        setNameSuggestions(result.name_suggestions);
        // 最初の候補を自動選択
        const firstSuggestion = result.name_suggestions[0];
        setSelectedNameIndex(0);
        onUpdate({
          displayName: firstSuggestion.display_name,
          displayNameEn: firstSuggestion.display_name_en,
          internalName: generateInternalName(firstSuggestion.display_name_en),
        });
      }

      if (result.tags && result.tags.length > 0) {
        onUpdate({ tags: result.tags });
      }
    } catch (err: unknown) {
      // キャンセル由来 (AbortError / axios CanceledError) は無視する
      if (isAbortError(err)) {
        return;
      }
      console.error('Failed to suggest name:', err);
    } finally {
      // キャンセル後や再実行後は別の (または既にクリア済みの) controller に入れ替わっている。
      // その場合にここで state を触ると後続リクエストのローディング表示を消してしまうため、
      // 自分が現役のときだけ後始末する。
      if (abortControllerRef.current === controller) {
        setIsSuggestingName(false);
        abortControllerRef.current = null;
      }
    }
  }, [data.personImageBase64, data.personMaterialId, data.personMimeType, data.ethnicity, data.prompt, onUpdate, suggestNameApi, prefetchKey, takePrefetchedNames]);

  // コンポーネントのクリーンアップ
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // 名前候補を選択
  const handleSelectName = useCallback((index: number) => {
    const suggestion = nameSuggestions[index];
    if (suggestion) {
      setSelectedNameIndex(index);
      onUpdate({
        displayName: suggestion.display_name,
        displayNameEn: suggestion.display_name_en,
        internalName: generateInternalName(suggestion.display_name_en),
      });
    }
  }, [nameSuggestions, onUpdate]);

  // 初回表示時に名前提案を実行（AI生成モードの場合で、まだ名前が設定されていない場合）
  // useRefでマウント時の自動実行を追跡
  const autoSuggestTriggeredRef = useRef(false);

  useEffect(() => {
    // 既に自動提案をトリガーした場合はスキップ
    if (autoSuggestTriggeredRef.current) return;

    if (data.personImageMode === 'generate' && data.personImageBase64 && !data.displayName && !data.displayNameEn) {
      autoSuggestTriggeredRef.current = true;
      handleSuggestName({ usePrefetched: true });
    }
  }, [data.personImageMode, data.personImageBase64, data.displayName, data.displayNameEn, handleSuggestName]);

  // 英語表示名が変更されたら内部名を自動更新
  const handleDisplayNameEnChange = useCallback((displayNameEn: string) => {
    onUpdate({
      displayNameEn,
      internalName: generateInternalName(displayNameEn),
    });
  }, [onUpdate]);

  // タグ入力時はローカルstateのみ更新
  const handleTagsChange = useCallback((value: string) => {
    setTagsInput(value);
  }, []);

  // フォーカスが外れた時にパースしてonUpdateに反映
  const handleTagsBlur = useCallback(() => {
    const tags = tagsInput
      .split(/[,、\n]/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    onUpdate({ tags });
    // パース後の整形されたタグ文字列で更新
    setTagsInput(tags.join(', '));
  }, [tagsInput, onUpdate]);

  // AIから提案されたタグで外部からdata.tagsが更新された場合に同期
  useEffect(() => {
    // 入力フォーカス中は更新しない（既にhandleTagsBlurで更新予定）
    const currentTags = tagsInput
      .split(/[,、\n]/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    // data.tagsと現在のローカルタグが異なる場合のみ同期
    if (JSON.stringify(currentTags) !== JSON.stringify(data.tags)) {
      setTagsInput(data.tags.join(', '));
    }
  }, [data.tags]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.stepContainer}>
      <div className={styles.compactForm}>
          {/* 名前提案ボタン */}
          <div className={styles.suggestSection}>
            <button
              type="button"
              className={`${styles.suggestButton} ${isSuggestingName && !isHoveringCancel ? 'ai-loading-active' : ''} ${isSuggestingName && isHoveringCancel ? styles.cancelButton : ''}`}
              onClick={isSuggestingName ? handleCancel : () => handleSuggestName()}
              onMouseEnter={() => isSuggestingName && setIsHoveringCancel(true)}
              onMouseLeave={() => setIsHoveringCancel(false)}
            >
              {!isSuggestingName && (
                <span
                  className={styles.suggestButtonIcon}
                  style={{ WebkitMaskImage: 'url(/reward_stars_2.svg)', maskImage: 'url(/reward_stars_2.svg)' }}
                  aria-hidden="true"
                />
              )}
              {isSuggestingName
                ? (isHoveringCancel ? 'キャンセル' : '名前を生成中...')
                : (data.displayName ? 'AIで名前を再生成' : 'AIで名前を生成')}
            </button>
          </div>

          {/* 名前候補リスト */}
          {nameSuggestions.length > 0 && (
            <div className={styles.nameSuggestionsSection}>
              <label className={styles.label}>名前候補（クリックで選択）</label>
              <div className={styles.nameSuggestionsList}>
                {nameSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`${styles.nameSuggestionItem} ${selectedNameIndex === index ? styles.nameSuggestionItemSelected : ''}`}
                    onClick={() => handleSelectName(index)}
                  >
                    <span className={styles.nameSuggestionJp}>{suggestion.display_name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 表示名（和名） */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              表示名 <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              value={data.displayName}
              onChange={(e) => onUpdate({ displayName: e.target.value })}
              placeholder="山田太郎"
              className={styles.input}
            />
          </div>

          {/* 英語表示名 */}
          <div className={styles.formGroup}>
            <label className={styles.label}>英語表示名</label>
            <input
              type="text"
              value={data.displayNameEn}
              onChange={(e) => handleDisplayNameEnChange(e.target.value)}
              placeholder="Taro Yamada"
              className={styles.input}
            />
          </div>

          {/* 内部名 */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              内部名 <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              value={data.internalName}
              onChange={(e) => onUpdate({ internalName: e.target.value })}
              placeholder="taro_yamada"
              className={styles.input}
            />
            <span className={styles.helpText}>
              英小文字で始まり、英小文字・数字・アンダースコアのみ
            </span>
          </div>

          {/* タグ */}
          <div className={styles.formGroup}>
            <label className={styles.label}>タグ</label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => handleTagsChange(e.target.value)}
              onBlur={handleTagsBlur}
              placeholder="ビジネス, 爽やか"
              className={styles.input}
            />
            <span className={styles.helpText}>カンマ区切りで複数入力</span>
          </div>
      </div>
    </div>
  );
}
